import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Heading,
  HStack,
  VStack,
  Text,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  Select,
} from "@chakra-ui/react";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineDocumentDuplicate,
} from "react-icons/hi";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import Navbar from "./Navbar";
import { useAuth } from "./AuthContext";
import DocumentDefinitionSelectById from "./DocumentDefinitionSelectById";
import HeroImage from "./images/stamps.png";

ModuleRegistry.registerModules([AllCommunityModule]);

const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
const DEFINITIONS_API = "https://documents.poc.mrc.ai/api/definitions";

export default function StudioHomepage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [creating, setCreating] = useState(false);
  const [documentDefinitions, setDocumentDefinitions] = useState({});
  const gridRef = useRef(null);

  // New document form state
  const [newDocName, setNewDocName] = useState("");
  const [newDocDefinitionId, setNewDocDefinitionId] = useState(null);

  // Fetch all documents
  useEffect(() => {
    fetchDocuments();
    fetchDocumentDefinitions();
  }, []);

  // Fetch when page, pageSize, or searchTerm changes
  useEffect(() => {
    fetchDocuments(currentPage, pageSize, searchTerm);
  }, [currentPage, pageSize, searchTerm]);

  const fetchDocumentDefinitions = async () => {
    try {
      const res = await fetch(DEFINITIONS_API);
      if (!res.ok) throw new Error("Failed to fetch document definitions");
      const data = await res.json();

      // Create a map of definitionId -> definition name
      const definitionsMap = {};
      if (Array.isArray(data)) {
        data.forEach(def => {
          if (def._id) {
            definitionsMap[def._id] = def.name || "Unnamed Definition";
          }
        });
      }
      setDocumentDefinitions(definitionsMap);
    } catch (err) {
      console.error("Error fetching document definitions:", err);
      toast({
        title: "Error loading document definitions",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const fetchDocuments = async (page = currentPage, size = pageSize, search = searchTerm) => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: size.toString(),
        sortBy: "updatedDate",
        sortDescending: "true"
      });

      if (search) {
        queryParams.append("search", search);
      }

      const res = await fetch(`${DOCS_API}?${queryParams}`);
      if (!res.ok) throw new Error("Failed to fetch documents");
      const data = await res.json();

      setDocuments(data.items || []);
      setTotalCount(data.pagination?.totalCount || 0);
    } catch (err) {
      console.error("Error fetching documents:", err);
      toast({
        title: "Error loading documents",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setNewDocName("");
    setNewDocDefinitionId(null);
    onOpen();
  };

  const handleCreateDocument = async () => {
    if (!newDocDefinitionId) {
      toast({
        title: "Document Definition Required",
        description: "Please select a document definition",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!newDocName.trim()) {
      toast({
        title: "Document Name Required",
        description: "Please enter a document name",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);

      // Debug user structure to find correct UUID field
      console.log("[StudioHomepage] User object:", user);
      console.log("[StudioHomepage] User structure:", {
        hasPlatformUser: !!user?.platformUser,
        platformUserKeys: user?.platformUser ? Object.keys(user.platformUser) : [],
        platformUser: user?.platformUser,
        linkedInId: user?.linkedInId,
        sub: user?.sub,
        email: user?.email,
      });

      const userId = user?._id ||
                     user?.platformUser?._id ||
                     user?.platformUser?.id ||
                     user?.linkedInId ||
                     user?.sub ||
                     user?.email ||
                     null;

      console.log("[StudioHomepage] Resolved userId:", userId);

      const payload = {
        definitionId: newDocDefinitionId,
        name: newDocName.trim(),
        createdBy: userId,
      };

      console.log("[StudioHomepage] Creating document with payload:", payload);

      const res = await fetch(DOCS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      console.log("[StudioHomepage] POST response status:", res.status, res.statusText);

      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        console.error("[StudioHomepage] Create failed:", errorText);
        throw new Error("Failed to create document");
      }

      const created = await res.json();

      console.log("[StudioHomepage] Document created - Full response:", created);
      console.log("[StudioHomepage] Document created - Key fields:", {
        id: created._id,
        name: created.name,
        definitionId: created.definitionId,
        hasName: !!created.name,
        nameValue: JSON.stringify(created.name),
      });

      toast({
        title: "Document created",
        status: "success",
        duration: 2000,
      });

      onClose();

      // Small delay to ensure document is persisted before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log("[StudioHomepage] Navigating to editor with docId:", created._id);

      // Navigate to the editor
      navigate(`/editor?doc=${created._id}`);
    } catch (err) {
      console.error("[StudioHomepage] Error creating document:", err);
      toast({
        title: "Error creating document",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleEditDocument = (docId) => {
    navigate(`/editor?doc=${docId}`);
  };

  const handleCopyDocument = async (docId) => {
    try {
      const res = await fetch(`${DOCS_API}/${docId}/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        console.error("[StudioHomepage] Copy failed:", errorText);
        throw new Error("Failed to copy document");
      }

      const copiedDoc = await res.json();

      toast({
        title: "Document copied",
        status: "success",
        duration: 2000,
      });

      // Small delay to ensure document is persisted before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Navigate to the newly copied document
      navigate(`/editor?doc=${copiedDoc._id}`);
    } catch (err) {
      console.error("[StudioHomepage] Error copying document:", err);
      toast({
        title: "Error copying document",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      const res = await fetch(`${DOCS_API}/${docId}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Failed to delete document");

      toast({
        title: "Document deleted",
        status: "success",
        duration: 2000,
      });

      // Refresh the list
      fetchDocuments();
    } catch (err) {
      console.error("Error deleting document:", err);
      toast({
        title: "Error deleting document",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "—";
    }
  };

  // AG Grid cell renderers
  const EditCellRenderer = (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Edit Document">
          <IconButton
            icon={<HiOutlinePencil />}
            size="sm"
            onClick={() => handleEditDocument(params.data._id)}
            aria-label="Edit Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const CopyCellRenderer = (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Copy Document">
          <IconButton
            icon={<HiOutlineDocumentDuplicate />}
            size="sm"
            colorScheme="teal"
            variant="ghost"
            onClick={() => handleCopyDocument(params.data._id)}
            aria-label="Copy Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const DeleteCellRenderer = (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Delete Document">
          <IconButton
            icon={<HiOutlineTrash />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => handleDeleteDocument(params.data._id)}
            aria-label="Delete Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const DateCellRenderer = (params) => {
    return formatDate(params.value);
  };

  const DefinitionCellRenderer = (params) => {
    return documentDefinitions[params.value] || "—";
  };

  // AG Grid column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "edit",
        cellRenderer: EditCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
        pinned: "left",
      },
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.name || "Untitled",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Document Definition",
        field: "definitionId",
        flex: 2,
        sortable: true,
        filter: true,
        cellRenderer: DefinitionCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Product Code",
        field: "productCode",
        flex: 1,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.productCode || "—",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Template",
        field: "isTemplate",
        width: 100,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.isTemplate ? "Yes" : "No",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Last Updated",
        field: "updatedDate",
        flex: 1,
        sortable: true,
        filter: "agDateColumnFilter",
        cellRenderer: DateCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.status || "—",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "copy",
        cellRenderer: CopyCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
      },
    ],
    [documentDefinitions]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
    }),
    []
  );

  // Note: gridRef is already declared at the top of the component

  if (loading) {
    return (
      <>
        <Navbar />
        <Center height="calc(100vh - 80px)">
          <Spinner size="xl" />
        </Center>
      </>
    );
  }

  return (
    <Box position="relative" minHeight="100vh">
      <Navbar />

      {/* Background image */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        zIndex={-1}
        pointerEvents="none"
      >
        <Box
          as="img"
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          filter="blur(1px)"
        />
      </Box>

      <Box p={8} maxWidth="1400px" mx="auto" position="relative" zIndex={1}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="xl">Studio Documents</Heading>
            <Button
              leftIcon={<HiOutlinePlus />}
              colorScheme="blue"
              onClick={handleOpenCreateModal}
            >
              Create
            </Button>
          </HStack>

          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <HiOutlineSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {documents.length === 0 ? (
            <Box
              borderWidth="1px"
              borderRadius="md"
              overflow="hidden"
              backgroundColor="white"
            >
              <Center p={8}>
                <VStack spacing={4}>
                  <Text color="gray.500" fontSize="lg">
                    No documents yet. Create your first document!
                  </Text>
                  <Button
                    leftIcon={<HiOutlinePlus />}
                    colorScheme="blue"
                    onClick={handleOpenCreateModal}
                  >
                    Create
                  </Button>
                </VStack>
              </Center>
            </Box>
          ) : (
            <>
              <Box
                className="ag-theme-quartz"
                style={{ height: "calc(100vh - 380px)", width: "100%" }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={documents}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowHeight={50}
                  pagination={false}
                  animateRows={true}
                  rowSelection="single"
                  getRowId={(params) => params.data._id}
                />
              </Box>

              {/* Custom Pagination Controls */}
              <Box
                mt={4}
                p={4}
                bg="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <HStack justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      isDisabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      isDisabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(p => p + 1)}
                      isDisabled={currentPage >= Math.ceil(totalCount / pageSize)}
                    >
                      Next
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                      isDisabled={currentPage >= Math.ceil(totalCount / pageSize)}
                    >
                      Last
                    </Button>
                  </HStack>
                  <HStack spacing={3}>
                    <Text fontSize="sm" fontWeight="medium">
                      Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}
                      {' '}({totalCount} total)
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="sm">Items per page:</Text>
                      <Select
                        size="sm"
                        w="80px"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </Select>
                    </HStack>
                  </HStack>
                </HStack>
              </Box>
            </>
          )}
        </VStack>
      </Box>

      {/* Create Document Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Document Name</FormLabel>
                <Input
                  placeholder="Enter document name..."
                  value={newDocName}
                  onChange={(e) => setNewDocName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleCreateDocument();
                    }
                  }}
                />
              </FormControl>

              <DocumentDefinitionSelectById
                label="Document Definition"
                selectedDocumentDefinitionId={newDocDefinitionId}
                onChange={(id) => setNewDocDefinitionId(id)}
              />
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={creating}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateDocument}
              isLoading={creating}
              loadingText="Creating..."
            >
              Create Document
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
